import React, { useEffect, useState } from "react";
import Loader from "../../../common/loader";
import CustomerCardView from "./customerCardView";
import CustomerTableView from "./customerTableView";
import AddCustomerModal from "./addCustomer";
import InviteCustomerDialog from "./inviteCustomer";
import {
  IconButton,
  Button,
  Paper,
  InputBase,
  Snackbar,
  Alert,
  Pagination,
  Typography,
  Box,
} from "@mui/material";
import {
  Search as SearchIcon,
  PersonAddAlt as PersonAddAltIcon,
  Email as EmailIcon,
  ViewModule as ViewModuleIcon,
  ViewList as ViewListIcon,
} from "@mui/icons-material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import {
  getCustomers,
  deleteCustomerById,
  blockCustomer,
} from "../../../services/customer.service";
import { Business } from "../../../validation/business.validation";
import { Customer } from "../../../validation/customer.validation";
import { getLoginUserData } from "../../../services/account.service";

const defaultBusiness: Business = {
  name: "",
  ein: "",
  email: "",
  phone: "",
  address: "",
  synonyms: "",
  website: "",
  id: "",
};

const Customers: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [view, setView] = useState<"grid" | "table">("grid");
  const [customers, setCustomers] = useState([]);
  const [query, setQuery] = useState({ page: 1, limit: 10, search: "", businessId: "" });
  const [paginationData, setPaginationData] = useState({
    page: 1,
    limit: 10,
    total: 0,
  });
  const [openModal, setOpenModal] = useState(false);
  const [user, setUser] = React.useState<Business>(defaultBusiness);
  const [editCustomerDetails, setEditCustomerDetails] = React.useState<Customer | null>(null);
  const [openInviteDialog, setOpenInviteDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error" | "info",
  });

  const totalPages = Math.ceil(paginationData.total / paginationData.limit);

  // Set default view based on screen size
  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth < 768; // md breakpoint
      setView(isMobile ? "grid" : "table");
    };

    handleResize(); // Set initial view
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const init = async () => {
      setIsLoading(true);
      try {
        const user = await getLoginUserData();
        if (user?.id) {
          setUser(user);
          const updatedQuery = { ...query, businessId: user?.businessId ? user?.businessId : user.id  };
          setQuery(updatedQuery);
          await fetchCustomers(updatedQuery);
        }
      } catch (error: any) {
        showSnackbar("error", error?.message || "Failed to load co-owners.");
      } finally {
        setIsLoading(false);
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (query.businessId) fetchCustomers(query);
    }, 700);
    return () => clearTimeout(delayDebounce);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.search]);

  const fetchCustomers = async (payload: any) => {
    setIsLoading(true);
    try {
      const res = await getCustomers(payload);
      setCustomers(res.data);
      setPaginationData({ page: res.page, limit: res.limit, total: res.total });
    } catch (error: any) {
      showSnackbar("error", error?.message || "Failed to fetch customers.");
    } finally {
      setIsLoading(false);
    }
  };

  const deleteCustomer = async (customer: any) => {
    setIsLoading(true);
    try {
      await deleteCustomerById(customer.id);
      showSnackbar("success", "Customer deleted successfully.");
      fetchCustomers(query);
    } catch (error: any) {
      showSnackbar("error", error?.message || "Failed to delete customer.");
    } finally {
      setIsLoading(false);
    }
  };

  const deactivateCustomer = async (customer: any) => {
    setIsLoading(true);
    try {
      await blockCustomer(customer.id, { isActive: !customer.isActive });
      showSnackbar(
        "success",
        `Customer ${
          customer.isActive ? "deactivated" : "activated"
        } successfully.`
      );
      fetchCustomers(query);
    } catch (error: any) {
      showSnackbar(
        "error",
        error?.message || "Failed to update customer status."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const editCustomer = async (customer: Customer) => {
    if (customer) {
      setOpenModal(true);
      setEditCustomerDetails(customer);
    }
  };

  const showSnackbar = (
    severity: "success" | "error" | "info",
    message: string
  ) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setQuery((prev) => ({ ...prev, search: event.target.value }));
  };

  const handlePageChange = (_: any, value: number) => {
    const updatedQuery = { ...query, page: value };
    setQuery(updatedQuery);
    fetchCustomers(updatedQuery);
  };

  return (
    <div>
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-4 py-2 px-4">
          <Paper
            component="form"
            className="flex items-center border border-gray-300 rounded-full px-2"
            elevation={0}
            sx={{
              borderRadius: 2,
              py: 0.5,
              fontWeight: 600,
              minWidth: { xs: '100%', sm: '350px' },
              maxWidth: { xs: '100%', sm: '450px' },
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            <SearchIcon className="text-gray-400 ml-2" />
            <InputBase
              sx={{ ml: 1, flex: 1 }}
              placeholder="Search customer"
              value={query.search}
              onChange={handleSearchChange}
            />
          </Paper>
          <div className="flex items-center flex-wrap gap-3">
            <Button
              variant="contained"
              startIcon={<PersonAddAltIcon />}
              onClick={() => setOpenModal(true)}
              sx={{
                borderRadius: 2,
                py: 0.5,
                fontWeight: 600,
                fontSize: "1rem",
              }}
            >
              Add Customer
            </Button>
            <Button
              variant="outlined"
              startIcon={<EmailIcon />}
              onClick={() => setOpenInviteDialog(true)}
              sx={{
                borderRadius: 2,
                py: 0.5,
                fontWeight: 600,
                fontSize: "1rem",
              }}
            >
              Invite Customer
            </Button>
            <div className="flex items-center gap-2 rounded-xl border border-gray-300 overflow-hidden">
              <IconButton
                onClick={() => setView("grid")}
                className={`!p-2 ${view === "grid" ? "bg-gray-100" : ""}`}
              >
                <ViewModuleIcon
                  className={view === "grid" ? "text-black" : "text-gray-500"}
                />
              </IconButton>
              <IconButton
                onClick={() => setView("table")}
                className={`!p-2 ${view === "table" ? "bg-gray-100" : ""}`}
              >
                <ViewListIcon
                  className={view === "table" ? "text-black" : "text-gray-500"}
                />
              </IconButton>
            </div>
          </div>
        </div>
        {isLoading ? (
          <Loader />
        ) : (
          <>
            {customers.length === 0 ? (
              <Box className="flex flex-col items-center justify-center py-32 text-center text-gray-500 bg-white rounded-lg border border-gray-200 mx-4">
                <div className="flex items-center justify-center mb-4">
                  <div className="animate-pulse">
                    <SearchRoundedIcon
                      className="text-gray-400"
                      sx={{ fontSize: 48 }}
                    />
                  </div>
                </div>
                <Typography variant="h6" className="mt-4 text-gray-700">
                  No Customers Found
                </Typography>
                <Typography variant="body2" className="text-gray-500">
                  You can try adjusting your search or add a new customer to get
                  started.
                </Typography>
              </Box>
            ) : view === "grid" ? (
              <CustomerCardView
                customers={customers}
                onEdit={editCustomer}
                onDelete={deleteCustomer}
                onBlock={deactivateCustomer}
                user={user}
              />
            ) : (
              <CustomerTableView
                customers={customers}
                onEdit={editCustomer}
                onDelete={deleteCustomer}
                onBlock={deactivateCustomer}
                user={user}
              />
            )}
            {customers.length > 0 && (
              <div className="flex flex-col md:flex-row justify-between md:items-center items-center px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg shadow-sm gap-2">
                <Pagination
                  count={totalPages}
                  page={paginationData.page}
                  onChange={handlePageChange}
                  color="primary"
                  shape="rounded"
                  size="medium"
                />
                <div className="text-sm text-gray-600 text-center md:text-left">
                  Showing {(paginationData.page - 1) * paginationData.limit + 1}{" "}
                  to{" "}
                  {Math.min(
                    paginationData.page * paginationData.limit,
                    paginationData.total
                  )}{" "}
                  of {paginationData.total} results
                </div>
              </div>
            )}
          </>
        )}
      </div>
      <AddCustomerModal
        open={openModal}
        handleClose={() => {
          setOpenModal(false);
          setEditCustomerDetails(null);
          fetchCustomers(query);
        }}
        businessId={query.businessId}
        editMode={!!editCustomerDetails}
        customerData={editCustomerDetails || undefined}
      />
      <InviteCustomerDialog
        open={openInviteDialog}
        onClose={() => setOpenInviteDialog(false)}
        user={user}
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default Customers;
